import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsent } from "@/components/layout/CookieConsent";
import Index from "./pages/Index";
import About from "./pages/About";
import OurStory from "./pages/OurStory";

import Photography from "./pages/Photography";
import Cinematography from "./pages/Cinematography";
import ComboPackages from "./pages/ComboPackages";
import Photobooth from "./pages/Photobooth";
import Portfolio from "./pages/Portfolio";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Auth from "./pages/Auth";
import SetupAdmin from "./pages/SetupAdmin";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Galleries from "./pages/Galleries";
import GalleryDetail from "./pages/GalleryDetail";
import NotFound from "./pages/NotFound";
import Chat from "./pages/Chat";

// Lazy load heavy admin pages (contains Tiptap, ProseMirror ~350KB)
const Admin = lazy(() => import("./pages/Admin"));
const PayrollDetail = lazy(() => import("./pages/PayrollDetail"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded routes
const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about-pixie-clicks" element={<About />} />
              <Route path="/our-story-philosophy" element={<OurStory />} />
              
              <Route path="/photography-by-pixie-clicks" element={<Photography />} />
              <Route path="/cinematography" element={<Cinematography />} />
              <Route path="/combo-packages" element={<ComboPackages />} />
              <Route path="/photobooth" element={<Photobooth />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><Admin /></Suspense>} />
              <Route path="/setup-admin" element={<SetupAdmin />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/galleries" element={<Galleries />} />
              <Route path="/galleries/:slug" element={<GalleryDetail />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/payroll/:payrollId" element={<Suspense fallback={<LazyFallback />}><PayrollDetail /></Suspense>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
